import { ChevronLeft, Lock, Star } from "lucide-react";
import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode } from "react";

export function Crest({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  return <span className={`crest crest--${size}`} aria-hidden="true"><span>✦</span></span>;
}

interface GameButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "gold" | "teal" | "ghost" | "danger" | "stone";
  icon?: ReactNode;
}

export function GameButton({ children, variant = "gold", icon, className = "", ...props }: PropsWithChildren<GameButtonProps>) {
  return <button className={`game-button game-button--${variant} ${className}`} {...props}>{icon}<span>{children}</span></button>;
}

export function Panel({ children, className = "", title }: PropsWithChildren<{ className?: string; title?: string }>) {
  return <section className={`ornate-panel ${className}`}>
    <i className="ornate-corner ornate-corner--tl" /><i className="ornate-corner ornate-corner--tr" />
    <i className="ornate-corner ornate-corner--bl" /><i className="ornate-corner ornate-corner--br" />
    {title && <h2 className="panel-title"><span>{title}</span></h2>}
    {children}
  </section>;
}

export function ScreenHeader({ title, eyebrow, onBack, right }: { title: string; eyebrow?: string; onBack: () => void; right?: ReactNode }) {
  return <header className="screen-header safe-top">
    <GameButton variant="ghost" icon={<ChevronLeft size={19} />} onClick={onBack} aria-label="Back" className="screen-header__back">{title === "" ? "Back" : ""}</GameButton>
    <div className="screen-header__title">{eyebrow && <span>{eyebrow}</span>}<h1>{title}</h1></div>
    <div className="screen-header__right">{right}</div>
  </header>;
}

export function StarRow({ count, size = 18 }: { count: number; size?: number }) {
  return <span className="star-row" aria-label={`${count} stars`}>{[0, 1, 2].map((index) => <Star key={index} size={size} fill={index < count ? "currentColor" : "none"} className={index < count ? "is-earned" : ""} />)}</span>;
}

export function LockedVeil({ label }: { label: string }) {
  return <div className="locked-veil"><Lock size={20} /><span>{label}</span></div>;
}

export function ProgressBar({ value, maximum, color = "#f0be50", label }: { value: number; maximum: number; color?: string; label?: string }) {
  const ratio = maximum > 0 ? Math.max(0, Math.min(1, value / maximum)) : 0;
  return <div className="progress-wrap">{label && <span>{label}</span>}<div className="progress-track"><i style={{ width: `${ratio * 100}%`, background: color }} /></div></div>;
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (next: boolean) => void; label: string }) {
  return <button type="button" className={`toggle ${checked ? "is-on" : ""}`} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}><span className="toggle__track"><i /></span><b>{label}</b></button>;
}
